import React, { ChangeEventHandler, ReactElement, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { RADOM_COLORS } from '../util/Constants'
import Tooltip from './Tooltip'
import { DropdownContentBody } from './Dropdown'

interface ITextInputWithSuggetsionsProps {
  label: string
  type?: any
  value: string | number
  pattern?: string
  placeholder?: string
  onChange: ChangeEventHandler<HTMLElement>
  onKeyDown?: React.KeyboardEventHandler
  setSelectedItems: (items: string[]) => void
  required?: boolean
  disabled?: boolean
  suggestionList: string[]
  inputStyle?: React.CSSProperties
  isInvalid?: boolean
  validationError?: string
}

export const IconButtonInner = styled.div`
  display: flex;
  gap: 8px;
  font-size: 12px;
  align-items: center;
  justify-content: center;
`

export const BaseInputStyles = `
  border: 1px solid #cfd7e5;
  background-color: white;
  border-radius: 8px;
  padding: 9px 12px;
  min-height: 42px;
  width: 100%;
  outline: none;
  box-shadow: none;
  transition: 0.2s ease all;
  color: #1f2937;
  font-size: 14px;
  line-height: 1.35;
  font-weight: 400;

  &:hover {
    border-color: #bcc6da;
  }

  :focus,
  :focus-visible {
    border: 1px solid ${RADOM_COLORS.BLUE5};
    box-shadow: 0 0 0 3px rgb(97 138 250 / 18%);
  }

  ::placeholder {
    color: #919db2;
  }
`

export const INPUT_ERROR_STYLE: React.CSSProperties = {
  borderColor: '#df1b41',
  boxShadow: '0 1px 1px rgba(15, 23, 42, 0.06), 0 0 0 2px rgba(223, 27, 65, 0.14)'
}

export const InlineInputError = styled.div`
  color: #df1b41;
  font-size: 12px;
  line-height: 1.35;
  margin-top: -2px;
`
const DropdownListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  cursor: pointer;

  :hover {
    background-color: ${RADOM_COLORS.GRAY_LIGHTEST};
  }
`

export const TextArea = styled.textarea<{ disabled?: boolean }>`
  ${BaseInputStyles}
  border-radius: 10px;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
  min-height: 50px;
`

export const OptionalLabel = styled.div`
  max-width: min-content;
  background-color: #f3f5fa;
  border-radius: 999px;
  padding: 2px 8px;
  border: 1px solid #e5e9f3;
  color: #6b7280;
  font-size: 10px;
  user-select: none;
`

export const TextAreaWithLabel = (
  { label, value, onChange, placeholder, onKeyDown, required, disabled }: React.HTMLProps<HTMLTextAreaElement>
): ReactElement => {
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 5 }}>
      <InputLabel>
        <span>{ label }</span>
      </InputLabel>
      {!required && <OptionalLabel>Optional</OptionalLabel>}
    </div>
    <TextArea
      style={{ maxHeight: 100 }}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      onKeyDown={onKeyDown}
      required={required}
      disabled={disabled}
    />
  </div>
}

interface TextWithLabelProps extends React.HTMLProps<HTMLInputElement> {
  tooltip?: string
  inputStyle?: React.CSSProperties
  isInvalid?: boolean
  validationError?: string
}

const TextInputWithLabel_ = (
  {
    label,
    tooltip,
    value,
    onChange,
    placeholder,
    pattern,
    onKeyDown,
    required,
    disabled,
    ref,
    as,
    style,
    inputStyle,
    isInvalid,
    validationError,
    ...props
  }: TextWithLabelProps
): ReactElement => {
  const hasValidationError = isInvalid === true || Boolean(validationError)

  return <div style={{ display: 'flex', flexDirection: 'column', gap: 10, ...style }}>
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 5 }}>
      <InputLabel><span>{label}</span></InputLabel>
      {!required && <OptionalLabel>Optional</OptionalLabel>}
      {tooltip && <Tooltip message={tooltip} />}
    </div>
    <TextInput
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      pattern={pattern}
      onKeyDown={onKeyDown}
      disabled={disabled}
      required={required}
      aria-invalid={hasValidationError}
      style={{ ...inputStyle, ...(hasValidationError ? INPUT_ERROR_STYLE : undefined) }}
      {...props}
    />
    {validationError && <InlineInputError>{validationError}</InlineInputError>}
  </div>
}

export const TextInputWithLabel = React.memo(TextInputWithLabel_)
export const TextInputWithLabelWithSuggestions = ({
  label, type = 'text', value, onChange, placeholder, pattern,
  onKeyDown, required, disabled, suggestionList, setSelectedItems, inputStyle, isInvalid, validationError
}: ITextInputWithSuggetsionsProps): ReactElement => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1)
  const listboxIdRef = useRef(`autocomplete-${Math.random().toString(36).slice(2, 9)}`)
  const hasValidationError = isInvalid === true || Boolean(validationError)

  const onItemSelect = (e: React.MouseEvent | React.KeyboardEvent, item: string): void => {
    e.preventDefault()
    e.stopPropagation()
    setSelectedItems([item])
    setShowSuggestions(false)
    setHighlightedIndex(-1)
  }

  useEffect(() => {
    const hasSuggestions = (suggestionList?.length ?? 0) > 0
    setShowSuggestions(hasSuggestions)
    setHighlightedIndex((currentIndex) => {
      if (!hasSuggestions) {
        return -1
      }

      if (currentIndex < 0) {
        return 0
      }

      if (currentIndex >= suggestionList.length) {
        return suggestionList.length - 1
      }

      return currentIndex
    })
  }, [suggestionList])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
        setHighlightedIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (!showSuggestions || highlightedIndex < 0) {
      return
    }

    const activeItemId = `${listboxIdRef.current}-${highlightedIndex}`
    document.getElementById(activeItemId)?.scrollIntoView({ block: 'nearest' })
  }, [showSuggestions, highlightedIndex])

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    const listLength = suggestionList?.length ?? 0
    const hasSuggestions = showSuggestions && listLength > 0

    if (hasSuggestions && e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((prev) => (prev + 1) % listLength)
      return
    }

    if (hasSuggestions && e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((prev) => (prev <= 0 ? listLength - 1 : prev - 1))
      return
    }

    if (hasSuggestions && e.key === 'Enter') {
      const indexToUse = highlightedIndex >= 0 ? highlightedIndex : 0
      const selected = suggestionList[indexToUse]
      if (selected) {
        onItemSelect(e, selected)
        return
      }
    }

    if (e.key === 'Escape') {
      setShowSuggestions(false)
      setHighlightedIndex(-1)
      return
    }

    onKeyDown?.(e)
  }

  return (
    <div
      ref={wrapperRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        position: 'relative',
        width: '100%'
      }}
    >
      {label && <InputLabel>{label}</InputLabel>}

      <TextInput
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        pattern={pattern}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if ((suggestionList?.length ?? 0) > 0) {
            setShowSuggestions(true)
            setHighlightedIndex(0)
          }
        }}
        required={required}
        disabled={disabled}
        aria-invalid={hasValidationError}
        style={{ ...inputStyle, ...(hasValidationError ? INPUT_ERROR_STYLE : undefined) }}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showSuggestions}
        aria-controls={listboxIdRef.current}
        aria-activedescendant={
          showSuggestions && highlightedIndex >= 0 ? `${listboxIdRef.current}-${highlightedIndex}` : undefined
        }
      />

      {validationError && <InlineInputError>{validationError}</InlineInputError>}

      {showSuggestions && (
        <div
          id={listboxIdRef.current}
          role="listbox"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            width: '100%',
            marginTop: 4,
            background: 'white',
            borderRadius: 5,
            boxShadow: `0 0 5px ${RADOM_COLORS.GRAY_DARK}`,
            zIndex: 999,
            fontSize: 14
          }}
        >
          <DropdownContentBody>
            {suggestionList?.map((item, index) => (
              <DropdownListItem
                id={`${listboxIdRef.current}-${index}`}
                key={item}
                onClick={(e) => onItemSelect(e, item)}
                role="option"
                aria-selected={index === highlightedIndex}
                style={{
                  backgroundColor: index === highlightedIndex ? RADOM_COLORS.GRAY_LIGHTEST : 'transparent'
                }}
              >
                {item}
              </DropdownListItem>
            ))}
          </DropdownContentBody>
        </div>
      )}
    </div>
  )
}

export const InputLabel = styled.span`
  font-weight: 400;
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  color: #5f6b7c;
  opacity: 1;
  font-size: 13px;
`

export const TextInput = styled.input<{ pattern?: string, disabled?: boolean }>`
  ${BaseInputStyles}

  ${({ pattern }) => pattern && `
    :invalid {
      border: 1px solid ${RADOM_COLORS.ERROR};
    }`
}
`

export const InputWithCheckbox = styled.div`
  padding: 6px;
  display: flex;
  gap: 2px;
  border: 1px solid ${RADOM_COLORS.GRAY_DARK};
  box-shadow: 0 1px 1px ${RADOM_COLORS.GRAY_LIGHTEST};
  border-radius: 4px;

  :focus-within {
    outline: 1px solid ${RADOM_COLORS.GRAY_MED};
  }
`

export const UnstyledInput = styled.input`
  border: 0;
  width: 100%;

  :focus {
    outline: none;
    border: none;
  }
`

export const Cookie = styled.div`
  max-width: min-content;
  background-color: ${RADOM_COLORS.GRAY_MED};
  border-radius: 5px;
  padding: 3px 5px;
  color: ${RADOM_COLORS.GRAY_DARKEST};
  font-size: 12px;
  user-select: none;
`
